import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  FiArrowRight,
  FiDownload,
  FiInfo,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";
import { LuFileText } from "react-icons/lu";
import PayoutBulkUploadFlowSteps from "./PayoutBulkUploadFlowSteps";
import { payoutApi } from "./api/payoutApiClient";
import {
  BULK_UPLOAD_SCHEMA_VERSION,
  buildHeaderAliases,
  transferBulkUploadFields,
} from "./api/payoutBulkUploadSchemas";
import {
  compactBody,
  pickString,
  rupeesToPaise,
  stableIdempotencyKey,
  writeSessionJson,
} from "./api/payoutMutationHelpers";
import {
  isValidBankAccountNumber,
  isValidIfsc,
  normalizeBankAccountNumber,
  normalizeIfsc,
  validateTransferModeForAmount,
} from "./api/payoutValidation";
import { showPayoutNotification } from "./PayoutNotification";

const sampleFilePath = "/payout-bulk-upload-sample.csv";
const sampleAmountsPaise = [10000, 10100, 10200, 10300];
const headerAliases = buildHeaderAliases(transferBulkUploadFields);

type PayoutAccountOption = {
  id: string;
  name: string;
  bank: string;
  accountNumber: string;
  ifsc: string;
};

const normalizeLookup = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const canonicalizeRow = (row: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(row).map(([key, value]) => [
      headerAliases[normalizeLookup(key)] || key,
      typeof value === "string" ? value.trim() : value,
    ]),
  );

const parseBulkFile = async (file: File) => {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return [];
  return XLSX.utils
    .sheet_to_json<Record<string, unknown>>(sheet, { defval: "", raw: false })
    .map(canonicalizeRow)
    .filter((row) => Object.values(row).some((value) => String(value ?? "").trim()));
};

const accountOptionFromApi = (account: Record<string, unknown>): PayoutAccountOption | null => {
  const id = pickString(account, ["account_id", "accountId", "id", "funding_account_id", "fundingAccountId"]);
  if (!id) return null;
  return {
    id,
    name: pickString(account, ["display_name", "displayName", "name", "account_name", "accountName"], id),
    bank: pickString(account, ["bank_name", "bankName", "bank"]),
    accountNumber: pickString(account, ["account_number", "accountNumber", "account_number_last4", "accountNumberLast4", "last4"]),
    ifsc: pickString(account, ["ifsc", "ifsc_code", "ifscCode"]),
  };
};

const accountLast4 = (accountNumber: string) => {
  const digits = accountNumber.replace(/\D/g, "");
  return digits ? digits.slice(-4) : accountNumber.slice(-4);
};

const accountLabel = (account: PayoutAccountOption) => {
  const last4 = accountLast4(account.accountNumber);
  return [account.name, account.bank, last4 ? `**** ${last4}` : ""]
    .filter(Boolean)
    .join(" | ");
};

const resolveBeneficiaryId = (
  row: Record<string, unknown>,
  beneficiaries: Record<string, unknown>[],
) => {
  const rawId = pickString(row, ["beneficiary_id"]);
  if (
    rawId &&
    beneficiaries.some((beneficiary) => pickString(beneficiary, ["beneficiary_id", "beneficiaryId", "id"]) === rawId)
  ) {
    return rawId;
  }

  const name = normalizeLookup(pickString(row, ["beneficiary_name"]));
  const ifsc = normalizeLookup(pickString(row, ["beneficiary_ifsc"]));
  const account = normalizeLookup(pickString(row, ["beneficiary_account_number"]));
  const matched = beneficiaries.find((beneficiary) => {
    const beneficiaryName = normalizeLookup(
      pickString(beneficiary, ["name_entered", "nameEntered", "beneficiary_name", "beneficiaryName", "name"]),
    );
    const beneficiaryIfsc = normalizeLookup(pickString(beneficiary, ["ifsc", "ifsc_code", "ifscCode"]));
    const beneficiaryLast4 = normalizeLookup(
      pickString(beneficiary, ["account_number_last4", "accountNumberLast4", "last4"]),
    );
    const nameMatches = name && beneficiaryName === name;
    const ifscMatches = !ifsc || beneficiaryIfsc === ifsc;
    const accountMatches = !account || account.endsWith(beneficiaryLast4);
    return nameMatches && ifscMatches && accountMatches;
  });

  return matched ? pickString(matched, ["beneficiary_id", "beneficiaryId", "id"]) : rawId;
};

const resolveRows = (
  rows: Record<string, unknown>[],
  payoutAccount: PayoutAccountOption,
  beneficiaries: Record<string, unknown>[],
) =>
  rows.map((row, index) => ({
    ...row,
    funding_account_id: payoutAccount.id,
    funding_account_name: payoutAccount.name,
    beneficiary_id: resolveBeneficiaryId(row, beneficiaries),
    amount_paise:
      Number(row.amount_paise) ||
      Number(row.amountPaise) ||
      rupeesToPaise(row.amount),
    mode: pickString(row, ["mode"], "NEFT").toUpperCase(),
    client_reference_id: pickString(
      row,
      ["client_reference_id"],
      `${pickString(row, ["beneficiary_name"], "bulk")}-${Date.now()}-${index + 1}`,
    ),
  }));

const bulkPaymentValidationItem = (row: Record<string, unknown>) =>
  compactBody({
    funding_account_id: pickString(row, ["funding_account_id", "fundingAccountId"]),
    funding_account_name: pickString(row, ["funding_account_name", "fundingAccountName"]),
    beneficiary_id: pickString(row, ["beneficiary_id", "beneficiaryId"]),
    beneficiary_name: pickString(row, ["beneficiary_name", "beneficiaryName"]),
    beneficiary_account_number: pickString(row, [
      "beneficiary_account_number",
      "beneficiaryAccountNumber",
      "account_number",
    ]),
    beneficiary_ifsc: pickString(row, ["beneficiary_ifsc", "beneficiaryIfsc", "ifsc"]),
    amount_paise: Number(row.amount_paise) || Number(row.amountPaise) || rupeesToPaise(row.amount),
    mode: pickString(row, ["mode"], "NEFT").toUpperCase(),
    client_reference_id: pickString(row, ["client_reference_id", "clientReferenceId"]),
    purpose: pickString(row, ["purpose"]),
  });

const validateParsedRows = (rows: Record<string, unknown>[]) => {
  const errors: string[] = [];
  const seenReferences = new Map<string, number>();

  rows.forEach((row, index) => {
    const rowNumber = index + 1;
    const fundingAccountId = pickString(row, ["funding_account_id", "fundingAccountId"]);
    const beneficiaryId = pickString(row, ["beneficiary_id", "beneficiaryId"]);
    const beneficiaryAccount = normalizeBankAccountNumber(pickString(row, [
      "beneficiary_account_number",
      "beneficiaryAccountNumber",
      "account_number",
    ]));
    const beneficiaryIfsc = normalizeIfsc(pickString(row, ["beneficiary_ifsc", "beneficiaryIfsc", "ifsc"]));
    const amountPaise = Number(row.amount_paise) || Number(row.amountPaise) || rupeesToPaise(row.amount);
    const mode = pickString(row, ["mode"], "NEFT").toUpperCase();
    const referenceId = pickString(row, ["client_reference_id", "clientReferenceId"]);

    if (!fundingAccountId) errors.push(`Row ${rowNumber}: payout account is required`);
    if (!beneficiaryId && !beneficiaryAccount) {
      errors.push(`Row ${rowNumber}: beneficiary account number is required`);
    }
    if (beneficiaryAccount && !isValidBankAccountNumber(beneficiaryAccount)) {
      errors.push(`Row ${rowNumber}: beneficiary account number must be 9-18 digits`);
    }
    if (!beneficiaryId && !beneficiaryIfsc) {
      errors.push(`Row ${rowNumber}: beneficiary IFSC is required`);
    }
    if (beneficiaryIfsc && !isValidIfsc(beneficiaryIfsc)) {
      errors.push(`Row ${rowNumber}: beneficiary IFSC is invalid`);
    }
    if (!Number.isInteger(amountPaise) || amountPaise <= 0) {
      errors.push(`Row ${rowNumber}: amount must be greater than 0`);
    }
    const modeError = validateTransferModeForAmount(amountPaise, mode);
    if (modeError) errors.push(`Row ${rowNumber}: ${modeError}`);
    if (!referenceId) {
      errors.push(`Row ${rowNumber}: reference is required`);
    } else {
      const firstSeen = seenReferences.get(referenceId);
      if (firstSeen !== undefined) {
        errors.push(`Row ${rowNumber}: duplicate reference also found in row ${firstSeen}`);
      } else {
        seenReferences.set(referenceId, rowNumber);
      }
    }
  });

  return errors;
};

const validationErrorMessage = (errors: string[]) => {
  const visible = errors.slice(0, 5).join("; ");
  return errors.length > 5 ? `${visible}; +${errors.length - 5} more` : visible;
};

const buildDatabaseSampleRows = (
  payoutAccount: PayoutAccountOption,
  beneficiaries: Record<string, unknown>[],
) => {
  const usableBeneficiaries = beneficiaries.filter(
    (beneficiary) => pickString(beneficiary, ["status"], "ACTIVE").toUpperCase() === "ACTIVE",
  );
  const selectedBeneficiaries = usableBeneficiaries.length
    ? usableBeneficiaries
    : beneficiaries;

  if (!selectedBeneficiaries.length) {
    throw new Error("Add at least one active beneficiary before using sample bulk data.");
  }

  return sampleAmountsPaise.map((amountPaise, index) => {
    const beneficiary = selectedBeneficiaries[index % selectedBeneficiaries.length];
    return {
      schema_version: BULK_UPLOAD_SCHEMA_VERSION,
      funding_account_id: payoutAccount.id,
      funding_account_name: payoutAccount.name,
      beneficiary_id: pickString(beneficiary, ["beneficiary_id", "beneficiaryId", "id"]),
      beneficiary_name: pickString(beneficiary, ["name_entered", "nameEntered", "beneficiary_name", "beneficiaryName", "name"]),
      beneficiary_ifsc: pickString(beneficiary, ["ifsc", "ifsc_code", "ifscCode"]),
      amount_paise: amountPaise,
      mode: amountPaise > 20000000 ? "RTGS" : "IMPS",
      category: pickString(beneficiary, ["category"], "Vendor"),
      purpose: "Bulk sample transfer",
      client_reference_id: `bulk_sample_${Date.now()}_${index + 1}`,
    };
  });
};

function PayoutBulkUploadTransfer() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [sampleSelected, setSampleSelected] = useState(false);
  const [accounts, setAccounts] = useState<PayoutAccountOption[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const selectedAccount = useMemo(
    () => accounts.find((account) => account.id === selectedAccountId) || null,
    [accounts, selectedAccountId],
  );

  useEffect(() => {
    if (!error) return;
    showPayoutNotification({
      tone: "error",
      title: "Bulk transfer upload failed",
      message: error,
    });
  }, [error]);

  useEffect(() => {
    let active = true;
    setAccountsLoading(true);
    payoutApi
      .accounts()
      .then((rows) => {
        if (!active) return;
        const options = rows
          .map((row) => accountOptionFromApi(row))
          .filter(Boolean) as PayoutAccountOption[];
        setAccounts(options);
        if (options.length === 1) {
          setSelectedAccountId(options[0].id);
        }
      })
      .catch(() => {
        if (active) setError("Unable to load payout bank accounts.");
      })
      .finally(() => {
        if (active) setAccountsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const chooseFile = (selectedFile?: File | null) => {
    if (!selectedFile) return;
    const validExtension = /\.(csv|xlsx)$/i.test(selectedFile.name);
    const validSize = selectedFile.size <= 10 * 1024 * 1024;

    if (!validExtension) {
      setError("Upload a CSV or XLSX file.");
      setFile(null);
      return;
    }
    if (!validSize) {
      setError("File size must be 10 MB or less.");
      setFile(null);
      return;
    }
    setError("");
    setSampleSelected(false);
    setFile(selectedFile);
  };

  const useSampleFile = () => {
    setError("");
    setSampleSelected(true);
    setFile(
      new File(["sample"], "payout-bulk-upload-sample.csv", {
        type: "text/csv",
      }),
    );
  };

  const uploadFile = async () => {
    if (!file) {
      setError("Bulk Upload Transfers: upload a transfer file or use the sample template before proceeding.");
      return;
    }
    if (!selectedAccount) {
      setError("Bulk Upload Transfers: select the payout bank account these transfers should debit.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const beneficiaries = await payoutApi.beneficiaries({ limit: 100 });
      const parsedRows = sampleSelected
        ? buildDatabaseSampleRows(selectedAccount, beneficiaries)
        : resolveRows(await parseBulkFile(file), selectedAccount, beneficiaries);
      if (!parsedRows.length) {
        throw new Error("No transfer rows found in this file.");
      }
      const localErrors = validateParsedRows(parsedRows);
      if (localErrors.length) {
        throw new Error(validationErrorMessage(localErrors));
      }
      const validation = await payoutApi.validateBulkPayments(
        { items: parsedRows.map(bulkPaymentValidationItem) },
        stableIdempotencyKey("validate-bulk-payments-upload", file.name),
      );
      const apiErrors = Array.isArray(validation.rows)
        ? validation.rows.flatMap((result) => {
            const rowResult = result as Record<string, unknown>;
            if (rowResult.ok === true) return [];
            const rowNumber = Number(rowResult.row_number) || "-";
            const errors = Array.isArray(rowResult.errors)
              ? rowResult.errors.filter((error): error is string => typeof error === "string")
              : [pickString(rowResult, ["status_label"], "Validation failed")];
            return errors.map((error) => `Row ${rowNumber}: ${error}`);
          })
        : [];
      if (apiErrors.length) {
        throw new Error(validationErrorMessage(apiErrors));
      }
      writeSessionJson("payoutBulkTransferDraft", {
        fileName: file.name,
        sizeKb: Math.max(1, Math.round(file.size / 1024)),
        payoutAccount: selectedAccount,
        rows: parsedRows,
      });
      navigate("/payouts/transfers/bulk-upload/review");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to read this bulk upload file.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            Bulk Upload Transfers
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            Upload a CSV or XLSX file to create multiple payout transfers.
          </p>
        </div>
        <Link
          to="/payouts/transfers"
          className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
        >
          Cancel
        </Link>
      </div>

      <PayoutBulkUploadFlowSteps activeStep={1} />

      <section className="max-w-[760px] rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx"
          className="hidden"
          onChange={(event) => chooseFile(event.target.files?.[0])}
        />
        <div className="mb-5">
          <label className="mb-2 block text-sm font-semibold text-[#505E8E]">
            Select Payout Bank Account *
          </label>
          <select
            value={selectedAccountId}
            onChange={(event) => {
              setSelectedAccountId(event.target.value);
              setError("");
            }}
            disabled={accountsLoading}
            className="h-12 w-full rounded-[6px] border border-[#E0E5F2] bg-white px-4 text-sm font-semibold text-[#1B163B] outline-none focus:border-[#6687FF]"
          >
            <option value="">
              {accountsLoading ? "Loading payout bank accounts..." : "Select payout bank account"}
            </option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {accountLabel(account)}
              </option>
            ))}
          </select>
          <p className="mt-2 text-xs font-medium text-[#505E8E]">
            Every transfer in this bulk upload will be initiated from the selected payout account.
          </p>
        </div>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            chooseFile(event.dataTransfer.files?.[0]);
          }}
          className="w-full rounded-[8px] border border-dashed border-[#A3AED0] bg-white px-6 py-12 text-center"
        >
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-[8px] bg-[#6687FF1A] text-[#6687FF]">
            <FiUploadCloud className="text-3xl" />
          </div>
          <p className="mt-5 text-sm font-semibold text-[#1B163B]">
            Drag and drop your file here or{" "}
            <span className="text-[#6687FF]">click to upload</span>
          </p>
          <p className="mt-2 text-xs font-medium text-[#505E8E]">
            Supported formats: CSV, XLSX. Maximum file size 10 MB.
          </p>
        </button>

        {false && (
          <div className="mt-4 rounded-[6px] border border-[#EF444433] bg-[#FEE2E2] px-4 py-3 text-sm font-semibold text-[#EF4444]">
            {error}
          </div>
        )}

        {file && (
          <div className="mt-4 flex items-center justify-between rounded-[6px] border border-[#E0E5F2] bg-white px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-[6px] bg-[#6687FF1A] text-[#6687FF]">
                <LuFileText />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1B163B]">
                  {file.name}
                </p>
                <p className="text-xs font-medium text-[#505E8E]">
                  {Math.max(1, Math.round(file.size / 1024))} KB
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setFile(null);
                setSampleSelected(false);
              }}
              className="text-[#505E8E]"
            >
              <FiX className="text-lg" />
            </button>
          </div>
        )}

        <div className="mt-5 rounded-[6px] bg-[#6687FF1A] px-4 py-4 text-sm font-medium text-[#6687FF]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="flex items-center gap-2">
              <FiInfo />
              Use sample schema v{BULK_UPLOAD_SCHEMA_VERSION} before uploading to avoid validation errors.
            </span>
            <span className="flex items-center gap-4">
              <button
                type="button"
                onClick={useSampleFile}
                className="inline-flex items-center gap-2 font-semibold"
              >
                <LuFileText />
                Use sample data
              </button>
              <a
                href={sampleFilePath}
                download
                className="inline-flex items-center gap-2 font-semibold"
              >
                <FiDownload />
                Download sample
              </a>
            </span>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-4">
          <Link
            to="/payouts/transfers"
            className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
          >
            Cancel
          </Link>
          <button
            type="button"
            onClick={uploadFile}
            disabled={uploading}
            style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
            className="flex h-10 items-center gap-3 rounded-[6px] bg-[#1B163B] px-7 text-sm font-semibold text-white disabled:opacity-50"
          >
            {uploading ? "Reading..." : "Upload File"}
            <FiArrowRight />
          </button>
        </div>
      </section>
    </div>
  );
}

export default PayoutBulkUploadTransfer;
